import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { useMemo } from 'react'
import { useSigners } from './useSigners'
import { CubeStatus } from 'Cube/domain/cube.domain.types'
import { useQuoteApproval } from 'Cube/domain/selectors/useQuoteApproval'

export enum PublishedStatus {
  Template = 'template',
  NotPublished = 'notPublished',
  WaitingForApproval = 'waitingForApproval',
  Published = 'published',
  PreparingSignatureRequest = 'preparingSignatureRequest',
  ReadyToSign = 'readyToSign',
  ProcessingSignatures = 'processingSignatures',
  Accepted = 'accepted',
  Executed = 'executed',
  Archived = 'archived'
}

export const usePublishedStatus = (): PublishedStatus => {
  const ctx = useCubeContext()
  const { pendingSigners } = useSigners()
  const { isApprovalPending } = useQuoteApproval()

  const isArchived = ctx.queries.rawData.data.common.isArchived

  const status = useMemo(() => {
    if (ctx.queries.quote.isATemplate) {
      return PublishedStatus.Template
    }

    if (isArchived) {
      return PublishedStatus.Archived
    }

    if (
      ctx.queries.rawData.data.common.status ===
      CubeStatus.QuotePreparingSignatureRequest
    ) {
      return PublishedStatus.PreparingSignatureRequest
    }

    if (ctx.queries.rawData.data.quote.executedAt) {
      return PublishedStatus.Executed
    }

    if (ctx.queries.rawData.data.quote.acceptedAt) {
      return PublishedStatus.Accepted
    }

    if (isApprovalPending) {
      return PublishedStatus.WaitingForApproval
    }

    if (ctx.queries.rawData.data.quote.readyToSignAt) {
      /**
       * If everyone has signed a quote, but the quote status hasn't changed to Accepted,
       * assume that Dropbox is still processing the final signature
       */
      if (pendingSigners.length === 0) {
        return PublishedStatus.ProcessingSignatures
      }

      return PublishedStatus.ReadyToSign
    }

    if (ctx.queries.rawData.data.quote.publishedAt) {
      return PublishedStatus.Published
    }

    return PublishedStatus.NotPublished
  }, [
    ctx.queries.rawData.data.quote.acceptedAt,
    ctx.queries.rawData.data.quote.executedAt,
    ctx.queries.rawData.data.quote.publishedAt,
    ctx.queries.rawData.data.quote.readyToSignAt,
    ctx.queries.rawData.data.common.status,
    ctx.queries.quote.isATemplate,
    isArchived,
    pendingSigners.length,
    isApprovalPending
  ])

  return status
}
